// ═══════════════════════════════════════════════════════════════════════
//   BLOCO · CONTRATOS E MATRÍCULAS (linha histórica do aluno)
//   MATRÍCULA → CONTRATO → TURMA → PLANO FINANCEIRO → DNA SIGN → TÍTULOS
// ═══════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader2, FileSignature, ChevronRight, AlertTriangle, ExternalLink,
  Wallet, GraduationCap, Receipt, RefreshCw, Printer, Download, PlusCircle, Ban,
} from 'lucide-react';
import { toast } from 'sonner';
import { GerarDnaSignDialog, AditivoDialog } from './ContratoAcoesDialogs';

const API_URL = window.location.origin;

const STATUS_STYLE = {
  RASCUNHO: 'dna-tone-neutral',
  PLANO_MONTADO: 'dna-tone-info',
  AGUARDANDO_DOCUMENTO: 'dna-tone-warning',
  AGUARDANDO_ASSINATURA: 'dna-tone-warning',
  ASSINADO: 'dna-tone-info',
  ATIVO: 'dna-tone-success',
  ENCERRADO: 'dna-tone-neutral',
  CANCELADO: 'dna-tone-danger',
};
const STATUS_LABEL = {
  RASCUNHO: 'Em preparação', PLANO_MONTADO: 'Plano montado',
  AGUARDANDO_DOCUMENTO: 'Aguardando documento', AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  ASSINADO: 'Assinado', ATIVO: 'Ativo', ENCERRADO: 'Encerrado', CANCELADO: 'Cancelado',
};
const SIGN_LABEL = {
  GENERATED: 'Documento gerado', SENT: 'Enviado para assinatura',
  SIGNING: 'Em assinatura', COMPLETED: 'Assinado', CANCELLED: 'Cancelado',
};

const money = (v) => (Number(v || 0)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dt = (v) => (v ? new Date(v).toLocaleDateString('pt-BR') : '—');

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-3 py-1 border-b border-dashed border-slate-100 last:border-0">
    <span className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</span>
    <span className="text-[13px] text-slate-800 font-medium text-right">{value ?? '—'}</span>
  </div>
);

const Panel = ({ icon: Icon, title, children }) => (
  <div className="rounded-xl border border-slate-200 bg-white p-3.5">
    <p className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-violet-700 mb-2">
      <Icon className="w-3.5 h-3.5" /> {title}
    </p>
    {children}
  </div>
);

export const ContratosBlock = ({ studentId, onOpenFinanceiro }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [signFor, setSignFor] = useState(null);
  const [aditivoFor, setAditivoFor] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(null);

  const load = useCallback(async (refresh = false) => {
    if (!studentId) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/student-contracts/${studentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Não foi possível carregar os contratos');
      const json = await res.json();
      setData(json);
      if (refresh) toast.success('Histórico de contratos atualizado');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { load(); }, [load]);

  // Imprimir / salvar o PDF do documento do DNA Sign (usa o cofre digital existente)
  const handlePdf = async (documentId, action) => {
    setPdfBusy(`${documentId}-${action}`);
    try {
      const token = localStorage.getItem('token');
      let res = await fetch(`${API_URL}/api/dna-sign/documents/${documentId}/download?kind=final`,
        { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        res = await fetch(`${API_URL}/api/dna-sign/documents/${documentId}/download?kind=original`,
          { headers: { Authorization: `Bearer ${token}` } });
      }
      if (!res.ok) throw new Error('PDF ainda não disponível para este documento');
      const url = URL.createObjectURL(await res.blob());
      if (action === 'save') {
        const a = document.createElement('a');
        a.href = url; a.download = `${documentId}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 8000);
      } else {
        const frame = document.createElement('iframe');
        frame.style.position = 'fixed'; frame.style.right = '0'; frame.style.bottom = '0';
        frame.style.width = '0'; frame.style.height = '0'; frame.style.border = '0';
        frame.src = url;
        frame.onload = () => { frame.contentWindow.focus(); frame.contentWindow.print(); };
        document.body.appendChild(frame);
        setTimeout(() => { URL.revokeObjectURL(url); frame.remove(); }, 60000);
      }
    } catch (e) {
      toast.error(e.message);
    } finally {
      setPdfBusy(null);
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-6" data-testid="contratos-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando linha histórica…
      </div>
    );
  }

  const contratos = data?.contratos || [];

  return (
    <div className="pt-4 space-y-3" data-testid="contratos-block">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-slate-500">
          Matrícula → Contrato → Turma → Plano financeiro → DNA Sign → Títulos.
          Cada contrato guarda exatamente o que foi contratado naquele ano.
        </p>
        <button
          type="button" onClick={() => load(true)}
          className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-violet-700 hover:text-violet-900 shrink-0"
          data-testid="contratos-refresh"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Atualizar
        </button>
      </div>

      {contratos.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500" data-testid="contratos-empty">
          Nenhum contrato encontrado para este aluno.
        </div>
      )}

      <div className="relative space-y-3">
        {contratos.map((c) => {
          const isOpen = openId === c.id;
          const snap = c.snapshot || {};
          const tit = c.titulos || {};
          return (
            <div key={c.id} className="relative pl-14" data-testid={`contrato-${c.academic_year}`}>
              {/* Ano na régua do tempo */}
              <div className="absolute left-0 top-3 w-12 text-right">
                <span className="text-lg font-extrabold text-slate-300 leading-none">{c.academic_year}</span>
              </div>
              <div className="absolute left-[52px] top-0 bottom-0 w-px bg-slate-200" />

              <div className={`rounded-2xl border bg-white overflow-hidden transition-shadow ${isOpen ? 'border-violet-300 shadow-md' : 'border-slate-200 hover:shadow-sm'}`}>
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : c.id)}
                  className="w-full text-left px-4 py-3 flex items-center gap-3 flex-wrap hover:bg-slate-50/60 transition-colors"
                  data-testid={`contrato-toggle-${c.academic_year}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-slate-900 flex items-center gap-2 flex-wrap">
                      Contrato {c.contract_number}
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATUS_STYLE[c.status] || STATUS_STYLE.RASCUNHO}`}>
                        {STATUS_LABEL[c.status] || c.status}
                      </span>
                      {c.type === 'rematricula'
                        ? <span className="text-[10px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">Rematrícula</span>
                        : <span className="text-[10px] text-slate-500 border border-slate-200 rounded-full px-2 py-0.5">Matrícula</span>}
                      {snap.financeiro_incompleto && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200" data-testid={`contrato-incompleto-${c.academic_year}`}>
                          <AlertTriangle className="w-3 h-3" /> Financeiro incompleto
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5 truncate">
                      {c.class_name || 'Turma não definida'}{c.segment ? ` · ${c.segment}` : ''}
                      {tit.quantidade ? ` · ${tit.quantidade} título(s)` : ''}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-slate-900">{money(snap.mensalidade || snap.total_mensal)}</p>
                    <p className="text-[10.5px] text-slate-400">mensalidade</p>
                  </div>
                  <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/60 p-3.5 grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid={`contrato-detalhe-${c.academic_year}`}>
                    <Panel icon={FileSignature} title="Contrato">
                      <Row label="Número" value={c.contract_number} />
                      <Row label="Ano letivo" value={c.academic_year} />
                      <Row label="Tipo" value={c.type === 'rematricula' ? 'Rematrícula' : 'Matrícula'} />
                      <Row label="Status" value={STATUS_LABEL[c.status] || c.status} />
                      <Row label="Criado em" value={dt(c.created_at)} />
                      <Row label="Origem" value={c.origin} />
                      <Row label="Responsável financeiro" value={c.financial_responsible?.name} />
                      {c.parent_contract_id && <Row label="Contrato anterior" value="vinculado" />}
                    </Panel>

                    <Panel icon={GraduationCap} title="Acadêmico">
                      <Row label="Turma" value={c.class_name} />
                      <Row label="Segmento" value={c.segment} />
                      <Row label="Turno" value={c.shift} />
                      <Row label="Curso" value={c.course_name} />
                    </Panel>

                    <Panel icon={Wallet} title="Financeiro (snapshot do ano)">
                      <Row label="Fonte" value={snap.fonte === 'plano_financeiro' ? 'Plano financeiro'
                        : snap.fonte === 'titulos_reais' ? 'Títulos reais (Safra/manuais)' : 'Indisponível'} />
                      <Row label="Matrícula/Rematrícula" value={money(snap.matricula)} />
                      <Row label="Mensalidade" value={money(snap.mensalidade)} />
                      <Row label="Anuidade" value={money(snap.anuidade)} />
                      {!!snap.material && <Row label="Material" value={money(snap.material)} />}
                      {!!snap.bilingue && <Row label="Bilíngue" value={money(snap.bilingue)} />}
                      {!!snap.integral && <Row label="Integral" value={money(snap.integral)} />}
                      {!!snap.almoco && <Row label="Almoço" value={money(snap.almoco)} />}
                      <Row label="Parcelas" value={snap.parcelas || '—'} />
                      <Row label="Vencimento" value={snap.dia_vencimento ? `dia ${snap.dia_vencimento}` : '—'} />
                      <Row label="Mês inicial" value={snap.mes_inicial || '—'} />
                      <Row label="Descontos" value={(snap.descontos || []).length
                        ? (snap.descontos || []).map(d => `${d.sobre}: ${d.tipo === 'percent' ? `${d.valor}%` : money(d.valor)}`).join(' · ')
                        : 'Nenhum'} />
                      <Row label="Serviços" value={(snap.servicos || []).map(s => s.label).join(' · ') || '—'} />
                      {c.snapshot_vigente?.tem_aditivo && (
                        <div className="mt-2 rounded-lg bg-violet-50 border border-violet-100 p-2">
                          <p className="text-[10px] font-bold uppercase tracking-wider text-violet-700 mb-1">
                            Valores em vigor (contrato + aditivos)
                          </p>
                          <Row label="Mensalidade vigente" value={money(c.snapshot_vigente.mensalidade)} />
                          <Row label="Integral vigente" value={money(c.snapshot_vigente.integral)} />
                          <Row label="Total mensal vigente" value={money(c.snapshot_vigente.total_mensal)} />
                          <p className="text-[10px] text-violet-600 mt-1">
                            O valor original assinado permanece intacto acima.
                          </p>
                        </div>
                      )}
                    </Panel>

                    <Panel icon={PlusCircle} title={`Aditivos (${(c.aditivos || []).length})`}>
                      {(c.aditivos || []).length === 0 && (
                        <p className="text-[12px] text-slate-400">Nenhum aditivo registrado neste contrato.</p>
                      )}
                      {(c.aditivos || []).map(a => (
                        <div key={a.id} className="py-1.5 border-b border-dashed border-slate-100 last:border-0"
                             data-testid={`aditivo-item-${a.numero}`}>
                          <p className="text-[12.5px] font-semibold text-slate-800 flex items-center gap-1.5">
                            {a.numero}
                            {a.status === 'CANCELADO' && (
                              <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200">
                                cancelado
                              </span>
                            )}
                            {!!a.valor_delta && (
                              <span className={`text-[11px] font-bold ${a.valor_delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                                {a.valor_delta > 0 ? '+' : ''}{money(a.valor_delta)}
                              </span>
                            )}
                          </p>
                          <p className="text-[11.5px] text-slate-500">{a.descricao}</p>
                          <p className="text-[10.5px] text-slate-400">
                            {a.categoria ? `${a.categoria} · ` : ''}{a.vigencia_inicio ? `vigência ${dt(a.vigencia_inicio)} · ` : ''}
                            registrado em {dt(a.created_at)}
                          </p>
                          {a.status !== 'CANCELADO' && (
                            <button
                              type="button"
                              onClick={async () => {
                                const motivo = window.prompt('Motivo do cancelamento do aditivo:');
                                if (motivo === null) return;
                                try {
                                  const token = localStorage.getItem('token');
                                  const res = await fetch(`${API_URL}/api/student-contracts/contract/${c.id}/addendum/${a.id}?motivo=${encodeURIComponent(motivo)}`,
                                    { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
                                  if (!res.ok) throw new Error('Falha ao cancelar aditivo');
                                  toast.success('Aditivo cancelado');
                                  load();
                                } catch (e) { toast.error(e.message); }
                              }}
                              className="text-[10.5px] text-slate-400 hover:text-rose-600 inline-flex items-center gap-1 mt-0.5"
                              data-testid={`aditivo-cancelar-${a.numero}`}
                            >
                              <Ban className="w-3 h-3" /> Cancelar aditivo
                            </button>
                          )}
                        </div>
                      ))}
                    </Panel>

                    <div className="space-y-3">
                      <Panel icon={FileSignature} title="DNA Sign">
                        <Row label="Documento" value={c.dna_sign?.title || (c.dna_sign?.document_id ? 'Gerado' : 'Não gerado')} />
                        <Row label="Status" value={SIGN_LABEL[c.dna_sign?.status] || '—'} />
                        <Row label="Assinatura"
                          value={(c.signature_mode || c.dna_sign?.signature_mode || '').toLowerCase() === 'physical'
                            ? '✓ Presencial (físico)' : dt(c.dna_sign?.signed_at)} />
                        {(c.signature_mode || c.dna_sign?.signature_mode || '').toLowerCase() === 'physical' && (
                          <Row label="Registrado em" value={dt(c.physical_signature?.registered_at || c.signed_at)} />
                        )}
                      </Panel>
                      <Panel icon={Receipt} title="Títulos">
                        <Row label="Quantidade" value={tit.quantidade ?? 0} />
                        <Row label="Pagos" value={tit.pagos ?? 0} />
                        <Row label="Pendentes" value={tit.pendentes ?? 0} />
                        <Row label="Vencidos" value={tit.vencidos ?? 0} />
                        <Row label="Valor total" value={money(tit.valor_total)} />
                        <Row label="Origem" value={(tit.origens || []).join(', ') || '—'} />
                      </Panel>
                    </div>

                    <div className="lg:col-span-2 flex items-center gap-2 flex-wrap">
                      <button
                        type="button" onClick={() => setSignFor(c)}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg bg-violet-600 text-white hover:bg-violet-700 transition-colors"
                        data-testid={`contrato-gerar-${c.academic_year}`}
                      >
                        <FileSignature className="w-3.5 h-3.5" /> Gerar contrato (DNA Sign)
                      </button>
                      <button
                        type="button" onClick={() => onOpenFinanceiro?.(c.id)}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-emerald-300 text-emerald-700 hover:bg-emerald-50 transition-colors"
                        data-testid={`contrato-abrir-financeiro-${c.academic_year}`}
                      >
                        <Wallet className="w-3.5 h-3.5" /> Abrir financeiro deste contrato
                      </button>
                      <button
                        type="button" onClick={() => setAditivoFor(c)}
                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-violet-300 text-violet-700 hover:bg-violet-50 transition-colors"
                        data-testid={`contrato-aditivo-${c.academic_year}`}
                      >
                        <PlusCircle className="w-3.5 h-3.5" /> Registrar aditivo
                      </button>
                      {c.dna_sign?.document_id && (
                        <>
                          <button
                            type="button" onClick={() => handlePdf(c.dna_sign.document_id, 'print')}
                            disabled={pdfBusy === `${c.dna_sign.document_id}-print`}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors disabled:opacity-50"
                            data-testid={`contrato-imprimir-${c.academic_year}`}
                          >
                            {pdfBusy === `${c.dna_sign.document_id}-print`
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Printer className="w-3.5 h-3.5" />} Imprimir
                          </button>
                          <button
                            type="button" onClick={() => handlePdf(c.dna_sign.document_id, 'save')}
                            disabled={pdfBusy === `${c.dna_sign.document_id}-save`}
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors disabled:opacity-50"
                            data-testid={`contrato-pdf-${c.academic_year}`}
                          >
                            {pdfBusy === `${c.dna_sign.document_id}-save`
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Download className="w-3.5 h-3.5" />} Salvar .pdf
                          </button>
                          <a
                            href="/v2/dna-sign/documentos" target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors"
                            data-testid={`contrato-ver-dnasign-${c.academic_year}`}
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> Ver no DNA Sign
                          </a>
                        </>
                      )}
                      {c.enrollment_process_id && (
                        <a
                          href="/v2/matriculas" target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors"
                          data-testid={`contrato-abrir-matricula-${c.academic_year}`}
                        >
                          <ExternalLink className="w-3.5 h-3.5" /> Abrir no DNA Matrículas
                        </a>
                      )}
                      {c.financial_plan_id && (
                        <a
                          href="/v2/financeiro/dnapay/titulos" target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-white transition-colors"
                          data-testid={`contrato-plano-${c.academic_year}`}
                        >
                          <Wallet className="w-3.5 h-3.5" /> Plano financeiro / títulos
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <GerarDnaSignDialog
        open={!!signFor} onOpenChange={(v) => !v && setSignFor(null)}
        contract={signFor || {}} onDone={load}
      />
      <AditivoDialog
        open={!!aditivoFor} onOpenChange={(v) => !v && setAditivoFor(null)}
        contract={aditivoFor || {}} onDone={load}
      />
    </div>
  );
};

export default ContratosBlock;
